class IngredientsGrid extends HTMLElement {
  #currentCategory = 'all';
  #currentQuery = '';
  #controller = new AbortController();

  /** @type {HTMLElement[]} */ #cards = [];
  /** @type {HTMLElement[]} */ #groups = [];
  /** @type {HTMLElement[]} */ #pills = [];
  /** @type {HTMLElement | null} */ #emptyState = null;

  /** @type {Map<Element, {categories: string[], name: string, group: HTMLElement | null}>} */
  #index = new Map();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#cards  = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('ingredient-card')]);
    this.#groups = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-letter-group]')]);
    this.#pills  = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-pill]')]);
    this.#emptyState = /** @type {HTMLElement | null} */ (this.querySelector('[data-empty-state]'));

    for (const card of this.#cards) {
      const raw = card.dataset.category || '';
      this.#index.set(card, {
        categories: raw ? raw.split(' ').filter(Boolean) : [],
        name: card.querySelector('.card-ingredient__name')?.textContent.toLowerCase().trim() ?? '',
        group: /** @type {HTMLElement | null} */ (card.closest('[data-letter-group]')),
      });
    }

    const params = new URLSearchParams(window.location.search);
    this.#currentCategory = params.get('category') || 'all';
    this.#currentQuery = params.get('q') || '';

    this.#syncPillUI();
    this.#filter();

    for (const pill of this.#pills) {
      pill.addEventListener('click', this.#onPillClick, { signal });
    }

    document.addEventListener('banner-search:query', this.#onSearchQuery, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#index.clear();
  }

  #onPillClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.currentTarget);
    this.#currentCategory = target.dataset.pill || 'all';
    this.#syncPillUI();
    this.#updateUrl();
    this.#filter();
  };

  #onSearchQuery = (/** @type {Event} */ event) => {
    const { query } = /** @type {CustomEvent<{query: string}>} */ (event).detail;
    this.#currentQuery = query || '';
    this.#filter();
  };

  #syncPillUI() {
    for (const pill of this.#pills) {
      const active = pill.dataset.pill === this.#currentCategory;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-pressed', String(active));
    }
  }

  #updateUrl() {
    const url = new URL(window.location.href);
    if (this.#currentCategory === 'all') {
      url.searchParams.delete('category');
    } else {
      url.searchParams.set('category', this.#currentCategory);
    }
    history.replaceState(null, '', url.toString());
  }

  #filter() {
    const query = this.#currentQuery.toLowerCase().trim();
    const category = this.#currentCategory;
    let anyVisible = false;
    const visibleGroups = new Set();

    for (const card of this.#cards) {
      const entry = this.#index.get(card);
      if (!entry) continue;

      const { categories, name, group } = entry;
      const visible =
        (category === 'all' || categories.includes(category)) &&
        (query === '' || name.includes(query));

      card.classList.toggle('is-filtered-out', !visible);
      card.setAttribute('aria-hidden', String(!visible));

      if (visible) {
        anyVisible = true;
        if (group) visibleGroups.add(group);
      }
    }

    for (const group of this.#groups) {
      group.classList.toggle('is-filtered-out', !visibleGroups.has(group));
    }

    if (this.#emptyState) {
      this.#emptyState.classList.toggle('is-filtered-out', anyVisible);
    }
  }
}

if (!customElements.get('ingredients-grid')) {
  customElements.define('ingredients-grid', IngredientsGrid);
}
